import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, apiError, TOKEN_KEY, USER_KEY } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem(USER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [booting, setBooting] = useState(Boolean(localStorage.getItem(TOKEN_KEY)));

  const persist = useCallback((token, nextUser) => {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(nextUser));
    setUser(nextUser);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
  }, []);

  const login = useCallback(
    async (email, password) => {
      const { data } = await api.post("/api/auth/login", { email, password });
      persist(data.access_token, data.user);
      return data.user;
    },
    [persist]
  );

  const refresh = useCallback(async () => {
    const { data } = await api.get("/api/auth/me");
    persist(null, data);
    return data;
  }, [persist]);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setBooting(false);
      return () => {};
    }
    api
      .get("/api/auth/me")
      .then(({ data }) => {
        if (!cancelled) persist(null, data);
      })
      .catch(() => {
        if (!cancelled) logout();
      })
      .finally(() => {
        if (!cancelled) setBooting(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(
    () => ({ user, booting, login, logout, refresh, setUser, apiError }),
    [user, booting, login, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
